import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import classnames from 'classnames';
import { dfs, getViewportBoundary, switchTypes, selectTypes, dateTypes, timeTypes } from '../../utils';
import { isValidType } from '@platform/template';

export default function useCellSelect(Table) {
    return class CellSelectTable extends Component{
        lastCellInfo = null;
        currentCellInfo = null; // 记录当前鼠标位置的单元格信息
        lastBodyUserSelect = null; // 保存body元素上的use-style样式
        batchRange = []; // 批改时的区间，用于样式

        render(){
            let { cellSelectConfig = {}, cellProps, rowProps, columns, store } = this.props;
            let {
                enable,
                batchSelectEnable,
                disabledCell,
                selectedCell = new Map(),
                onSelect,
                onChange,
            } = cellSelectConfig;

            if (enable) {
                // 加边框
                columns = dfs(columns, column => {
                    if (column.isLeaf) {
                        let oldRender = column.render;
                        column = {
                            ...column,
                            render: (row, rowIndex) => {
                                return <>
                                    {oldRender?.(row, rowIndex)}
                                    {this.isSelected(row, column) && <div className="selector-border">
                                        {
                                            batchSelectEnable && 
                                            !disabledCell?.(row.key, column.key) && 
                                            isValidType(column.itemtype) && 
                                            this.isSingleSelect() && 
                                            <div 
                                                className="batch-selector"
                                                onMouseDown={() => {
                                                    this.isBatchSelecting = true;
                                                    this.lastCursor = document.body.style.cursor;
                                                    document.body.style.cursor = 'crosshair';

                                                    let handleMouseUp = () => {
                                                        document.body.style.cursor = this.lastCursor;
                                                        this.lastCursor = null;
                                                        document.removeEventListener('mouseup', handleMouseUp);
                                                    };

                                                    document.addEventListener('mouseup', handleMouseUp);
                                                }}
                                            ></div>
                                        }
                                    </div>}
                                </>;
                            },
                        };
                    }
                    return column;
                });

                return (
                    <SelectModal {...this.props}>
                        <Table 
                            {...this.props}
                            rowProps={(row, rowIndex) => {
                                let rawRowProps = rowProps?.(row, rowIndex) ?? {};
                                let onClick = e => {
                                    let startCell = this.lastCellInfo,
                                        endCell = this.currentCellInfo;
                                    if (startCell && endCell && startCell.row.key === endCell.row.key && startCell.col.key === endCell.col.key && !e.shiftKey && !e.ctrlKey) {
                                        // 只有单纯点击单元格才走rowClick
                                        rawRowProps?.onClick(e);
                                    }
                                };
                                if (selectedCell.has(row.key)) {
                                    return {
                                        ...rawRowProps,
                                        className: classnames(
                                            rawRowProps.className,
                                            'selected-row',
                                        ),
                                        onClick,
                                    };
                                }
                                return {
                                    ...rawRowProps,
                                    onClick,
                                };
                            }}
                            cellProps={(rowInfo, colInfo) => {
                                let { row, rowIndex, rows } = rowInfo,
                                    { col, colIndex, columns } = colInfo,
                                    rawCellProps = rowIndex < 0 ? {} : cellProps?.(rowInfo, colInfo) ?? {}; // 负数是表头的，不要执行回调
    
                                // if (!isValidType(col.itemtype)) {
                                //     return rawCellProps;
                                // }

                                let prevRowIndex = rowIndex - 1,
                                    nextRowIndex = rowIndex + 1,
                                    nextColIndex = colIndex + 1;

                                let cellInfo = { ...rowInfo, ...colInfo };
    
                                return {
                                    ...rawCellProps,
                                    // 框选
                                    onMouseDownCapture: event => {
                                        // 点击激活状态的单元格时，不触发框选
                                        // 因为需要选中文字
                                        let activeCell = store.getActiveCell();

                                        if (activeCell.rowKey === row.key && activeCell.colKey === col.key) {
                                            this.preventBatchSelect = true;
                                        } else {
                                            this.preventBatchSelect = false;
                                        }

                                        if (!findDOMNode(this).contains(event.target)) return;

                                        if (event.button !== 0 && selectedCell?.get(row.key)?.has(col.key)) return;
                                        
                                        if (!event.shiftKey) {
                                            this.lastCellInfo = cellInfo;
                                            this.snapshot = null;
                                        } else {
                                            if (!this.snapshot) {
                                                this.snapshot = JSON.parse(JSON.stringify(selectedCell));
                                            }
                                            if (!this.lastCellInfo) { // 直接shift选择，需要选中首行至选择行
                                                this.lastCellInfo = {
                                                    rowIndex: 0,
                                                    colIndex: 0,
                                                    columns,
                                                    rows,
                                                    row: rows[0],
                                                    col: columns[0],
                                                };
                                            }
                                        }
                                    },
                                    // 批改
                                    onMouseMove: event => {
                                        if (!findDOMNode(this).contains(event.target)) return;

                                        if (event.button !== 0 && selectedCell?.get(row.key)?.has(col.key)) return;

                                        if (this.isBatchSelecting) {
                                            event.stopPropagation();
                                            event.preventDefault();
                                        }
                                        this.currentCellInfo = cellInfo;
                                    },
                                    onMouseUp: event => {
                                        if (!findDOMNode(this).contains(event.target)) return;
                                        
                                        if (event.button !== 0 && selectedCell?.get(row.key)?.has(col.key)) return;

                                        if (!this.lastCellInfo) {
                                            return;
                                        }

                                        // 批改
                                        if (this.isBatchSelecting) {
                                            this.isBatchSelecting = false;

                                            let startIndex = this.lastCellInfo.row.viewIndex,
                                                endIndex = this.currentCellInfo.row.viewIndex,
                                                selectedRows,
                                                oldValues = [];

                                            if (startIndex > endIndex) {
                                                selectedRows = rows.slice(endIndex, startIndex + 1);
                                            } else {
                                                selectedRows = rows.slice(startIndex, endIndex + 1);
                                            }

                                            selectedRows = selectedRows.map(row => {
                                                let colKey = this.lastCellInfo.col.key;
                                                oldValues.push(row.values[colKey]);
                                                return {
                                                    ...row,
                                                    values: {
                                                        ...row.values,
                                                        [colKey]: selectedRows[0].values[this.lastCellInfo.col.key],
                                                    },
                                                };
                                            });

                                            // selectedRows是更新后的值
                                            onChange?.(selectedRows, { 
                                                column: this.lastCellInfo.col, 
                                                row: rows[startIndex], 
                                                oldValues, 
                                                texts: selectedRows.map(() => {
                                                    let cellValue = selectedRows[0].values[this.lastCellInfo.col.key],
                                                        itemtype = this.lastCellInfo.col.itemtype;
                                                    
                                                    if (switchTypes.has(itemtype)) {
                                                        if (cellValue?.value === 'Y' || cellValue?.value === true) {
                                                            return true;
                                                        }
                                                        return false;
                                                    } else if (
                                                        itemtype === 'number' || 
                                                        selectTypes.has(itemtype) || 
                                                        dateTypes.has(itemtype) || 
                                                        timeTypes.has(itemtype)
                                                    ) {
                                                        return cellValue?.value ?? '';
                                                    } else {
                                                        return cellValue?._display_ ?? '';
                                                    }
                                                }),
                                            });

                                            let startCell = this.lastCellInfo,
                                                // 只能批改当列的
                                                endCell = { ...cellInfo, col: this.lastCellInfo.col, colIndex: this.lastCellInfo.colIndex };

                                            selectedCell = this.rangeSelect(
                                                startCell, 
                                                endCell, 
                                                { pattern: 'replace' },
                                            );

                                            onSelect?.(selectedCell, { startCell, endCell, batchSelect: true });
                                            return;
                                        }

                                        if (this.preventBatchSelect) {
                                            this.preventBatchSelect = false;
                                            return;
                                        }

                                        let action;

                                        if (event.shiftKey) {
                                            action = 'shift';

                                            event.preventDefault();
                                            selectedCell = this.rangeSelect(this.lastCellInfo, cellInfo, { pattern: 'merge' });
                                        } else if (event.metaKey || event.ctrlKey) {
                                            action = 'ctrl';

                                            // 连续单选
                                            event.preventDefault();
                                            selectedCell = this.rangeSelect(this.lastCellInfo, cellInfo, { pattern: 'toggle' });
                                        } else {
                                            action = 'down';
                                            
                                            // 单选 && 框选
                                            selectedCell = this.rangeSelect(this.lastCellInfo, cellInfo, { pattern: 'replace' });
                                        }
                                        onSelect?.(selectedCell, { startCell: this.lastCellInfo, endCell: cellInfo, action });
                                    },
                                    // rowIndex小于0是表头
                                    className: rowIndex < 0 ? classnames(
                                        { 'selected-cell': this.isSelected(row, col) },
                                    ) : classnames(
                                        rawCellProps.className,
                                        'selected-cell-enabled', 
                                        { 
                                            'selected-cell': this.isSelected(row, col),
                                            'no-border-right': nextColIndex < columns.length && this.isSelected(row, columns[nextColIndex]),
                                            'no-border-top': prevRowIndex > -1 && this.isSelected(rows[prevRowIndex], col),
                                            'no-border-bottom': nextRowIndex < rows.length && this.isSelected(rows[nextRowIndex], col),
                                        },
                                    ),
                                };
                            }} 
                            columns={columns}
                        />
                    </SelectModal>
                );
            } else {
                return <Table {...this.props}></Table>;
            }
        }

        componentDidMount() {
            window.addEventListener('mousedown', this.outsideClick);            
        }

        componentWillUnmount() {
            window.removeEventListener('mousedown', this.outsideClick);            
        }

        outsideClick = event => {
            let { cellSelectConfig = {} } = this.props,
                { onSelect, selectedCell } = cellSelectConfig;

            if (!findDOMNode(this).querySelector('table.table-next-body')?.contains(event.target) && !findDOMNode(this).querySelector('table.table-next-header')?.contains(event.target)) {
                this.lastCellInfo = null;
                if (selectedCell?.size) {
                    if (findDOMNode(this).contains(event.target)) {
                        onSelect?.(new Map(), { startCell: null, endCell: null, action: 'down' });
                    } else {
                        onSelect?.(new Map(), { startCell: null, endCell: null, action: null });
                    }
                }
            }
        }

        isSelected = (row, col) => {

            let { cellSelectConfig } = this.props,
                { selectedCell = new Map() } = cellSelectConfig,
                rowKey = row.key,
                colKey = col.key;
            return selectedCell?.get(rowKey)?.has(colKey);
        }

        isSingleSelect = () => {
            let { cellSelectConfig } = this.props,
                { selectedCell = new Map() } = cellSelectConfig;
            
            selectedCell = [...selectedCell];

            return selectedCell.length === 1 && selectedCell[0][1].size === 1;
        }

        rangeSelect = (startInfo = {}, endInfo, { pattern }) => {
            let {
                    rows,
                    columns,
                    rowIndex: firstRow,
                    colIndex: firstCol,
                } = startInfo, 
                {
                    rowIndex: lastRow,
                    colIndex: lastCol,
                } = endInfo,
                { cellSelectConfig = {}, rowKey: getRowKey } = this.props;
            
            let { selectedCell = new Map(), exclude = [] } = cellSelectConfig;
            exclude = new Set(exclude);
            
            if (this.snapshot) {
                selectedCell = new Map(this.snapshot.map(key_value => {
                    key_value = [...key_value];
                    key_value[1] = new Set(key_value[1]);
                    return key_value;
                }));
            }

            if (pattern === 'replace') {
                selectedCell = new Map();
            }

            if (firstRow > lastRow) {
                [firstRow, lastRow] = [lastRow, firstRow];
            }

            if (firstCol > lastCol) {
                [firstCol, lastCol] = [lastCol, firstCol];
            }

            // 框选单个表头，不处理
            if (firstRow === lastRow && firstCol === lastCol && firstRow < 0) {
                return selectedCell;
            }

            for(let rowIndex = firstRow; rowIndex <= lastRow; rowIndex++) {
                let rowKey;
                if (rowIndex < 0) { // 表头的rowKey用索引表示
                    rowKey = rowIndex;
                } else {
                    rowKey = getRowKey(rows[rowIndex], rowIndex);
                }
                for(let colIndex = firstCol; colIndex <= lastCol; colIndex++) {
                    let colKey = columns[colIndex].key,
                        rowSelectInfo = selectedCell.get(rowKey);
                        
                    if (exclude.has(colKey) || !columns[colIndex].selectable) {
                        continue;
                    }
                    
                    if (rowSelectInfo) {
                        if (pattern === 'toggle') {
                            rowSelectInfo.has(colKey) ? rowSelectInfo.delete(colKey) : rowSelectInfo.add(colKey);
                            if (!rowSelectInfo.size) { // 如果空集合就删除吧
                                selectedCell.delete(rowKey);
                            }
                        } else {
                            rowSelectInfo.add(colKey);
                        }
                    } else {
                        selectedCell.set(rowKey, new Set([colKey]));
                    }
                }
            }

            this.startInfo = null;
            this.endInfo = null;

            return selectedCell;
        }
    };
}

useCellSelect.displayName = 'useCellSelect';
useCellSelect.dependence = [];
useCellSelect.hooks = function() {
    this.store.selectedCell = new Map();
    return {
        setSelectedCell: selectedCell => {
            this.store.selectedCell = selectedCell || new Map();
            this.forceUpdate();
        },
        getSelectedCell: () => {
            return this.store.selectedCell;
        },
    };
};
useCellSelect.propsGenerator = function(props) {
    let { cellSelectConfig = {}, store } = props;

    let { enable, batchSelectEnable, onSelect, beforeSelect } = cellSelectConfig;

    if (!enable) {
        return props;
    }

    cellSelectConfig = { 
        ...cellSelectConfig,
        batchSelectEnable: batchSelectEnable && store.getStatus() === 'edit',
        selectedCell: store.getSelectedCell(),
        disabledCell: (rowKey, colKey) => {
            return store.getCellProps(rowKey, colKey, 'disabled');
        },
        onSelect: (selectedCell, { startCell, endCell, batchSelect, action }) => {
            if (beforeSelect && !beforeSelect(action)) return;

            store.setSelectedCell(selectedCell);
            // 框选单个单元格不触发onSelect（特指action为down的，shift/ctrl等除外）
            if (!(startCell && endCell && startCell.row.key === endCell.row.key && startCell.col.key === endCell.col.key && action === 'down')) {
                onSelect?.(selectedCell, { startCell, endCell, batchSelect, action });
            }
        },
    };

    return {
        ...props,
        cellSelectConfig,
    };
};
class SelectModal extends Component {
    lastBodyUserSelect = null;
    
    render() {
        let { flex, store } = this.props;
        return <div
            className={classnames("cell-selector-wrapper", { 'flex-container': flex })}
            onMouseDownCapture={event => {
                // 点击激活状态的单元格时，不触发框选
                // 因为需要选中文字
                let activeCellInstance = store.getInstance().getComponent(
                        store.getInstance().itemIDManager.getID(store.getActiveCell()),
                    ),
                    activeCell = activeCellInstance && findDOMNode(activeCellInstance.current);
                
                if (activeCell?.contains(event.target)) {
                    return;
                }

                // TableBody和tableHeader可框选
                let isHeaderArea = event.currentTarget.querySelector('.table-next-header-wrapper')?.contains(event.target);
                if (
                    (!event.currentTarget.querySelector('.table-next-body-wrapper')?.contains(event.target) &&
                    !isHeaderArea) ||
                    event.target.className.includes('column-resize-handler')
                ) {
                    return;
                }
                let startX, startY;
                startX = this.startX = event.clientX, startY = this.startY = event.clientY;

                // this.lastBodyUserSelect = document.body.style.userSelect;
                // document.body.style.userSelect = 'none';

                let modal = document.createElement('div');
                modal.style.position = 'fixed';
                modal.style.pointerEvents = 'none';
                modal.style.background = 'rgba(50, 128, 252, 0.2)';
                modal.style.zIndex = 9999;
                document.body.appendChild(modal);

                let clientWidth = document.documentElement.clientWidth,
                    clientHeight = document.documentElement.clientHeight;

                const handleMouseMove = event => {
                    event.preventDefault();

                    let endX = event.clientX, endY = event.clientY;

                    if (endX > startX) {``;
                        modal.style.width = `${endX - startX}px`;
                        modal.style.left = `${startX}px`;
                        modal.style.right = 'auto';
                    } else {
                        modal.style.width = `${startX - endX}px`;
                        modal.style.right = `${clientWidth - startX}px`;
                        modal.style.left = 'auto';
                    }

                    if (endY > startY) {
                        modal.style.height = `${endY - startY}px`;
                        modal.style.top = `${startY}px`;
                        modal.style.bottom = 'auto';
                    } else {
                        modal.style.height = `${startY - endY}px`;
                        modal.style.bottom = `${clientHeight - startY}px`;
                        modal.style.top = 'auto';
                    }

                    this.boundaryScroll(event);
                };

                const handleMouseUp = () => {
                    // document.body.style.userSelect = this.lastBodyUserSelect;
                    // this.lastBodyUserSelect = null;

                    document.body.removeChild(modal);
                    
                    window.removeEventListener('mousemove', handleMouseMove);
                    window.removeEventListener('mouseup', handleMouseUp);

                    this.readyToScroll = false;
                };

                window.addEventListener('mousemove', handleMouseMove);
                window.addEventListener('mouseup', handleMouseUp);
            }}
        >
            {this.props.children}
        </div>;
    }

    /**
     * 移到上/下边缘时，滚动
     * 触发条件：
     *  - 起始位置在视口内
     *  - 鼠标位置在边缘
     *  - 滚动方向和拖动方向一致
     *  - 拖动距离达到一定数值
     */
    boundaryScroll = event => {
        // console.log(this.getBoundary(), event.clientX, event.clientY);
        let { top, bottom, left, right } = this.getBoundary(),
            x = event.clientX, y = event.clientY;

        if (this.startX < left || this.startX > right || this.startY < top || this.startY > bottom) {
            // 起始位置不在视口时，return
            return;
        }

        let handlerX = findDOMNode(this).querySelector('.horizontal-scroll-bar'),
            handlerY = findDOMNode(this).querySelector('.vertical-scroll-bar');

        this.readyToScroll = false;

        if (x < left + 40 && this.startX - x > 20) {
            // console.log('左');
            this.readyToScroll = true;
            this.scrollAnimation(() => {
                handlerX.scrollLeft -= 1;
            });
        } else if (x > right - 40 && x - this.startX > 20) {
            // console.log('右');
            this.readyToScroll = true;
            this.scrollAnimation(() => {
                handlerX.scrollLeft += 1;
            });
        }
        if (y < top + 40 && this.startY - y > 20) {
            // console.log('上');
            this.readyToScroll = true;
            this.scrollAnimation(() => {
                handlerY.scrollTop -= 1;
            });
        } else if (y > bottom - 40 && y - this.startY > 20) {
            // console.log('下');
            this.readyToScroll = true;
            this.scrollAnimation(() => {
                handlerY.scrollTop += 1;
            });
        }
    }

    scrollAnimation = callback => {
        this.raf = requestAnimationFrame(() => {
            callback();
            this.readyToScroll && this.scrollAnimation(callback);
        });
    }

    getBoundary = () => {
        return getViewportBoundary(findDOMNode(this));
    }
}